import { useState, useRef } from "react";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { useTranslation } from "react-i18next";
import {
  colorAccent,
  colorBorder,
  colorTextPrimary,
  radiusButton,
} from "src/design/tokens";
import { GalleryItem } from "../types";
import { ImageGrid } from "./ImageGrid";
import { enforceImageRatio, fileToDataUrl } from "../utils/imageUtils";

interface Props {
  galleryImages: GalleryItem[];
  menuImages: GalleryItem[];
  isEditing: boolean;
  onDeleteGallery: (item: GalleryItem) => void;
  onAddGallery: (items: GalleryItem[]) => void;
  onDeleteMenu: (item: GalleryItem) => void;
  onAddMenu: (items: GalleryItem[]) => void;
}

export const GalleryTabsSection = ({
  galleryImages,
  menuImages,
  isEditing,
  onDeleteGallery,
  onAddGallery,
  onDeleteMenu,
  onAddMenu,
}: Props) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const isGalleryTab = activeTab === 0;
  const currentImages = isGalleryTab ? galleryImages : menuImages;
  const onAdd = isGalleryTab ? onAddGallery : onAddMenu;

  const handleUploadFiles = async (files: FileList) => {
    const processed: GalleryItem[] = [];
    for (const file of Array.from(files)) {
      const resized = await enforceImageRatio(file);
      const data_url = await fileToDataUrl(resized);
      processed.push({ file: resized, data_url, isPending: true });
    }
    onAdd(processed);
  };

  const sectionTitle = isGalleryTab
    ? t("gallery.restaurantPhotos.title")
    : t("gallery.menuPhotos.title");

  const sectionSubtitle = isGalleryTab
    ? t("gallery.restaurantPhotos.subtitle")
    : t("gallery.menuPhotos.subtitle");

  return (
    <Box>
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.length) {
            handleUploadFiles(e.target.files);
            e.target.value = "";
          }
        }}
      />

      {/* Tabs bar */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          borderBottom: `1px solid ${colorBorder}`,
          mb: 2,
          minHeight: 40,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            minHeight: 40,
            px: 2,
          },
          "& .Mui-selected": { color: colorAccent, fontWeight: 600 },
          "& .MuiTabs-indicator": { backgroundColor: colorAccent },
        }}
      >
        <Tab
          label={`${t("gallery.restaurantTab")} (${galleryImages.filter((i) => !i.isPending).length})`}
          icon={<CollectionsOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
        <Tab
          label={`${t("gallery.menuTab")} (${menuImages.filter((i) => !i.isPending).length})`}
          icon={<MenuBookOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
      </Tabs>

      {/* Section header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: colorTextPrimary, mb: 0.25 }}
          >
            {sectionTitle}
          </Typography>
          <Typography variant="caption" sx={{ color: "#6B7280" }}>
            {sectionSubtitle}
          </Typography>
        </Box>

        {isEditing && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => uploadInputRef.current?.click()}
            sx={{ textTransform: "none", borderRadius: radiusButton, flexShrink: 0, ml: 2 }}
          >
            {t("gallery.uploadPhotos")}
          </Button>
        )}
      </Box>

      {/* Image grid */}
      <ImageGrid
        images={currentImages}
        isEditing={isEditing}
        multiple
        onDelete={isGalleryTab ? onDeleteGallery : onDeleteMenu}
        onAdd={onAdd}
      />
    </Box>
  );
};
